import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { ConsumerCategory } from '@emm/core/models/consumer-category.model';
import { PinOverrideGroup } from '@emm/core/models/pin-override-group.model';
import { LanguageAssignmentsService } from './language-assignments.service';
import {ItemIdMap} from '@emm/core/models/item-id-mapping.model';
import {PinOverride} from '@emm/core/models/pin-override.model';
import {AttributeGroupService} from '@emm/core/services/attribute-group.service';
import { AttributeGroup } from '@emm/core/models/attribute-group.model';
import {AttributeService} from '@emm/core/services/attribute.service';
import {ConsumerManagerService} from '@emm/core/services/consumer-manager.service';
import {LockItemService} from '@emm/core/services/lock-item.service';
import {ConsumerService} from '@emm/core/services/consumer.service';
import {ItemGroupingService} from '@emm/core/services/item-grouping.service';
import {ItemIdMappingsService} from '@emm/core/services/item-id-mappings.service';
import {ItemService} from '@emm/core/services/item.service';
import {ItemTypeClassService} from '@emm/core/services/item-type-class.service';
import {ItemTypeService} from '@emm/core/services/item-type.service';
import {LocalesService} from '@emm/core/services/locales.service';
import {LocationSetService} from '@emm/core/services/location-set.service';
import {LocationSetTypeService} from '@emm/core/services/location-set-type.service';
import {LocationStatusService} from '@emm/core/services/location-status.service';
import {MasterMenuSystemService} from '@emm/core/services/master-menu-system.service';
import {MenuService} from '@emm/core/services/menu.service';
import {PinOverridesService} from '@emm/core/services/pin-overrides.service';
import {NutritionService} from '@emm/core/services/nutrition.service';
import {VenueService} from '@emm/core/services/venue.service';
import {TogglzService} from '@emm/core/services/togglz.service';
import {ItemLocaleResponse, Locale} from '@emm/core/models/locale.model';
import { Attribute } from '@emm/core/models/attribute.model';
import {Item} from '@emm/core/models/item.model';
import {ItemType} from '@emm/core/models/item-type.model';
import {LocationSetType} from '@emm/core/models/location-set-type.model';
import {Consumer} from '@emm/core/models/consumer.model';
import {ConsumerType} from '@emm/core/models/consumer-type.model';
import {ItemTypeClass} from '@emm/core/models/item-type-class.model';
import {AttributeType} from '@emm/core/models/attribute-type.model';
import {LocationStatus} from '@emm/core/models/location-status.model';
import {OperatorLockItem} from '@emm/core/models/operator-lock-item.model';
import {LocationSet} from '@emm/core/models/location-set.model';
import {NutritionAssignment, NutritionSaveRequest} from '@emm/core/models/nutrition.model';
import {LanguageAssignments} from '@emm/core/models/language-assignments.model';
import {Venue} from '@emm/core/models/venue.model';
import {ItemDetail} from '@emm/core/models/item-detail.model';
import {ItemSummary} from '@emm/core/models/item-summary.model';
import {Screen} from '@emm/core/models/screen.model';
import {take} from 'rxjs/operators';
import {SalesChannel} from '@emm/core/models/sales-channel.model';
import {SalesChannelsService} from '@emm/core/services/sales-channels.service';
import {NutritionPublishingService} from '@emm/core/services/nutrition-publishing.service';

@Injectable({
    providedIn: 'root',
})
export class ManagementApiService {
    constructor(
        private attributeService: AttributeService,
        private attributeGroupService: AttributeGroupService,
        private consumerManagerService: ConsumerManagerService,
        private operatorLockService: LockItemService,
        private consumerService: ConsumerService,
        private itemGroupingService: ItemGroupingService,
        private itemIdMappingsService: ItemIdMappingsService,
        private itemService: ItemService,
        private itemTypeClassService: ItemTypeClassService,
        private itemTypeService: ItemTypeService,
        private localesService: LocalesService,
        private locationSetService: LocationSetService,
        private locationSetTypeService: LocationSetTypeService,
        private locationStatusService: LocationStatusService,
        private masterMenuSystemService: MasterMenuSystemService,
        private menuService: MenuService,
        private pinOverridesService: PinOverridesService,
        private nutritionService: NutritionService,
        private languageAssignmentsService: LanguageAssignmentsService,
        private venueService: VenueService,
        private togglzService: TogglzService,
        private salesChannelsService: SalesChannelsService,
        private nutritionPublishingService: NutritionPublishingService,
    ) { }

    public getScreens(): Observable<Screen[]> {
        // Needed since getting from routes.ts causes circular dep, unless containers import services manually

        return of([
            // For routes to work as a nested route, we need to add both a route & nested route. The top level route
            // must have the name & selected properties. Define the child route with a name and selected. For example,
            // Item Groupings is both a route and a child route of the nav grouping, Items. The child route contains the
            // actual path to the component. The top level route is used to render the screen name in the management
            // header.

            // If a route doesn't have both a route and child route, it will cause an error @ line number 8 in the
            // ManagementLayoutComponent markup. This is because the screen selector component is expecting a top level
            // definition for the screen name and a child route definition for the actual path to the component. The
            // screen selector component filters the screens array to only render the valid top level screens.


            // Items nav grouping
            {
                name: 'Items', selected: true, route: 'items',
                children: [
                    { name: 'Items', selected: true, route: 'items' },
                    { name: 'Item Groupings', selected: false, route: 'item-groupings' }
                ]
            },
            { name: 'Item Groupings', selected: false, route: 'item-groupings' },

            // Attributes nav grouping
            { name: 'Attributes',
                children: [
                    { name: 'Attributes', selected: false, route: 'attributes' },
                    { name: 'Attribute Groups', selected: false, route: 'attribute-groups' },
                    { name: 'Item Types', selected: false, route: 'item-types' },
                ] },
            { name: 'Item Types', selected: false },
            { name: 'Attribute Groups', selected: false },

            // Location Sets nav grouping
            { name: 'Location Sets',
                children: [
                    { name: 'Location Sets', selected: false, route: 'location-sets' },
                    { name: 'Location Set Types', selected: false, route: 'location-set-types' },
                ] },
            { name: 'Location Set Types', selected: false },

            // Consumers nav grouping
            { name: 'Consumers',
                children : [
                    { name: 'Consumer Types', selected: false, route: 'consumer-types' },
                    { name: 'Consumer Manager', selected: false, route: 'consumer-manager' },
                ] },
            { name: 'Consumer Types', selected: false },
            { name: 'Consumer Manager', selected: false },

            // Master Menu System nav grouping
            { name: 'Master Menu System',
                children : [
                    { name: 'MMS Admin Override', selected: false, route: 'mms-admin-override',},
                    { name: 'MMS Revote', selected: false, route: 'mms-revote' },
                ]},
            { name: 'MMS Admin Override', selected: false},
            { name: 'MMS Revote', selected: false },

            // Non-nav grouping routes
            { name: 'Operator Locks', selected: false, route: 'operator-locks' },
            { name: 'Operations', selected: false, route: 'operations' },
            { name: 'Nutrition Assignments', selected: false, route: 'nutrition-assignments' },
            { name: 'PIN Overrides', selected: false, route: 'pin-overrides' },
            { name: 'Language Assignments', selected: false, route: 'language-assignments'},
            { name: 'Xref Id Mappings', selected: false, route: 'item-id-mappings'}
        ]);
    }

    public getLocaleOptions(): Observable<Locale[]> {
        return this.localesService.getLocaleOptions();
    }

    public getAllGlobalAttributes(): Observable<Attribute[]> {
        return this.attributeService.getAllGlobalAttributes();
    }

    public getAttributeExclusionsByConsumer(consumerTag: string): Observable<string[]> {
        return this.attributeService.getAttributeExclusionsByConsumer(consumerTag);
    }

    public updateAttributeExclusionsByConsumer(consumerTag: string, attributeTags: string[]) {
        return this.attributeService.updateAttributeExclusionsByConsumer(consumerTag, attributeTags);
    }

    public getItemsBySize(size: number): Observable<Item[]> {
        return this.itemService.getItemsBySize(size);
    }

    public getAllItems(): Observable<Item[]> {
        return this.itemService.getAllItems();
    }

    public duplicateItem(oldItemTag: string, newItemTag: string): Observable<any> {
        return this.itemService.duplicateItem(oldItemTag, newItemTag);
    }

    public getAllRevotes(): Observable<any[]> {
        return this.masterMenuSystemService.getRevotes();
    }

    public getRevote(id): Observable<any[]> {
        return this.masterMenuSystemService.getRevote(id);
    }

    public updateRevote(id: string, updatedRevote): Observable<any> {
        return this.masterMenuSystemService.updateRevote(id, updatedRevote);
    }

    public deleteRevote(id): Observable<any> {
        return this.masterMenuSystemService.deleteRevote(id);
    }

    public getAllCategories(consumer: any): Observable<any[]> {
        return this.masterMenuSystemService.getAllCategories(consumer);
    }

    public getAllLocationsMMS(consumer: any): Observable<any[]> {
        return this.locationSetService.getLocationsByParentMMS(consumer);
    }

    public getMenuSource(storeNum: any): Observable<any> {
        return this.locationSetService.getMenuSource(storeNum);
    }

    public getAllItemTypes(): Observable<ItemType[]> {
        return this.itemTypeService.getAllItemTypes();
    }

    public getAllLocationSetTypes(): Observable<LocationSetType[]> {
        return this.locationSetTypeService.getAllLocationSetTypes();
    }

    public getAllAttributes(): Observable<Attribute[]> {
        return this.attributeService.getAllAttributes();
    }

    public getConsumerCategories(): Observable<ConsumerCategory[]> {
        return this.consumerManagerService.getConsumerCategories();
    }

    public getConsumersForCategory(id: string): Observable<Consumer[]> {
        return this.consumerManagerService.getConsumersForCategory(id);
    }

    getAllConsumers(): Observable<Consumer[]> {
        return this.consumerService.getAllConsumers();
    }

    public getConsumer(id: string): Observable<Consumer> {
        return this.consumerManagerService.getConsumerDetails(id);
    }

    public deleteConsumer(consumerTag: string): Observable<object> {
        return this.consumerManagerService.deleteConsumer(consumerTag);
    }

    public updateConsumer(consumerTag: string, updates: Consumer): Observable<object> {
        return this.consumerManagerService.updateConsumer(consumerTag, updates);
    }

    public getConsumerTypes(): Observable<ConsumerType[]> {
        return this.consumerService.getConsumerTypes();
    }

    public getItem(tag: string): Observable<Item> {
        return this.itemService.getItem(tag);
    }

    public getItemLocale(tag: string, country: string, language: string): Observable<ItemLocaleResponse> {
        return this.itemService.getItemLocale(tag, country, language);
    }

    // todo: eol?
    public updateItem(tag: string, updates: any): Observable<Item> {
        return this.itemService.updateItem(tag, updates);
    }

    public getAllItemTypesForItems(): Observable<ItemType[]> {
        return this.itemTypeService.getAllItemTypesForItems();
    }

    public getAllItemTypesForItemGroups(): Observable<ItemType[]> {
        return this.itemTypeService.getAllItemTypesForItemGroups();
    }

    public deleteItem(tag: string): Observable<any> {
        return this.itemService.deleteItem(tag);
    }

    public setEnabled(tag: string, enabled: boolean): Observable<any> {
        return this.itemService.setEnabled(tag, enabled);
    }

    public getAllItemTypeClasses(): Observable<ItemTypeClass[]> {
        return this.itemTypeClassService.getAllItemTypeClasses();
    }

    public getItemType(itemTypeTag: string): Observable<ItemType> {
        return this.itemTypeService.getItemType(itemTypeTag);
    }

    public updateItemType(itemTypeTag: string, itemType: ItemType): Observable<ItemType> {
        return this.itemTypeService.updateItemType(itemTypeTag, itemType);
    }

    public createItemType(itemType: ItemType): Observable<any> {
        return this.itemTypeService.createItemType(itemType);
    }

    public deleteItemType(itemTypeTag: string): Observable<any> {
        return this.itemTypeService.deleteItemType(itemTypeTag);
    }

    public deleteLocationSetType(locationSetTypeTag: string): Observable<any> {
        return this.locationSetTypeService.deleteLocationSetType(locationSetTypeTag);
    }

    public createLocationSetType(locationSetType: any): Observable<any> {
        return this.locationSetTypeService.createLocationSetType(locationSetType);
    }

    public getLocationSetType(locationSetTypeTag: string): Observable<LocationSetType> {
        return this.locationSetTypeService.getLocationSetType(locationSetTypeTag);
    }

    public updateLocationSetType(
        locationSetTypeTag: string,
        locationSetType: LocationSetType
    ): Observable<LocationSetType> {
        return this.locationSetTypeService.updateLocationSetType(locationSetTypeTag, locationSetType);
    }

    public getAttribute(attributeTag: string): Observable<Attribute> {
        return this.attributeService.getAttribute(attributeTag);
    }

    public updateAttribute(attribute: Attribute): Observable<Attribute> {
        return this.attributeService.updateAttribute(attribute);
    }

    public getAllAttributeGroups(): Observable<AttributeGroup[]> {
        return this.attributeGroupService.getAttributeGroups();
    }

    public createAttributeGroup(attributeGroup: AttributeGroup): Observable<AttributeGroup> {
        return this.attributeGroupService.createAttributeGroup(attributeGroup);
    }

    public getAttributeGroup(attributeGroup: AttributeGroup): Observable<AttributeGroup> {
        return this.attributeGroupService.getAttributeGroup(attributeGroup);
    }

    public updateAttributeGroup(attributeGroupId: string, attributeGroup: AttributeGroup): Observable<AttributeGroup> {
        return this.attributeGroupService.updateAttributeGroup(attributeGroupId, attributeGroup);
    }

    public deleteAttributeGroup(attributeGroup: AttributeGroup): Observable<AttributeGroup> {
        return this.attributeGroupService.deleteAttributeGroup(attributeGroup);
    }

    public createAttribute(attribute: {
        attributeGroup: string,
        defaultValue: string;
        description: any;
        jsonProperty: any;
        localeSupport: boolean;
        locked: boolean;
        name: any;
        options: any;
        required: boolean;
        scope: any;
        sortWeight: any;
        tag: any;
        typetag: any;
    }): Observable<any> {
        return this.attributeService.createAttribute(attribute);
    }

    public deleteAttribute(attributeTag: string): Observable<any> {
        return this.attributeService.deleteAttribute(attributeTag);
    }

    public getAllAttributeTypes(): Observable<AttributeType[]> {
        return this.attributeService.getAllAttributeTypes();
    }

    public getEmergencyStatuses(): Observable<LocationStatus[]> {
        return this.locationStatusService.getEmergencyStatuses();
    }

    public getAllStatuses(): Observable<LocationStatus[]> {
        return this.locationStatusService.getAllStatuses();
    }

    public getItemsByEmergency(emergency: string): Observable<OperatorLockItem[]> {
        return this.operatorLockService.getItemsByEmergency(emergency);
    }

    public getAllChildItems(tag: string): Observable<Item[]> {
        return this.itemGroupingService.getAllChildItems(tag);
    }

    public addEmergencyItems(emergencyTag: string, items: Array<Item>): Observable<any> {
        return this.operatorLockService.addEmergencyItems(emergencyTag, items);
    }

    public uploadOperatorLockItems(emergencyTag: string, file: FormData): Observable<any> {
        return this.operatorLockService.uploadOperatorLockItems(emergencyTag, file);
    }

    public deleteEmergencyItem(emergencyTag: string, itemTag: string): Observable<any> {
        return this.operatorLockService.deleteEmergencyItem(emergencyTag, itemTag);
    }

    downloadSampleFile(): Observable<any> {
        return this.operatorLockService.downloadSampleFile();
    }

    public updateStatus(emergencyTag: string, statusMessage: string, active: boolean, subType?: string): Observable<any> {
        if (subType && subType === 'MESSAGING') {
            return this.locationStatusService.updateStatusMessage(emergencyTag, statusMessage, active, subType);
        } else {
            return this.locationStatusService.updateStatus(emergencyTag, active);
        }
    }

    public getAllLocationSetTypesForEdit(): Observable<LocationSetType[]> {
        return this.locationSetTypeService.getAllLocationSetTypesForEdit();
    }

    public getItemGroupings(): Observable<Item[]> {
        return this.itemGroupingService.getItemGroupings();
    }

    public addChildItems(parentTag: string, children: Item[], isGlobalAdd: boolean): Observable<any> {
        return this.itemService.addChildItems(parentTag, children, isGlobalAdd);
    }

    public removeChildItem(
        parentTag: string,
        childTag: string
    ): Observable<any> {
        return this.itemGroupingService.removeChildItem(parentTag, childTag);
    }

    public getAllLocationSetsByTag(tag: string): Observable<LocationSet[]> {
        return this.locationSetService.getAllLocationSetsByTag(tag);
    }

    public getLocationsByParent(parentTag: string): Observable<LocationSet[]> {
        return this.locationSetService.getLocationsByParent(parentTag);
    }

    public updateLocationSet(tag: string, updates: any): Observable<LocationSet> {
        return this.locationSetService.updateLocationSet(tag, updates).pipe(take(1));
    }

    public deleteLocationSet(tag: string): Observable<any> {
        return this.locationSetService.deleteLocationSet(tag);
    }

    public removeChildLocation(parentTag: string, childTag: string): Observable<any> {
        return this.locationSetService.removeChildLocation(parentTag, childTag);
    }

    public removeChildLocations(parentTag: string, childTags: string[]): Observable<any> {
        return this.locationSetService.removeChildLocations(parentTag, childTags);
    }

    public addChildLocations(parentTag: string, childTags: any): Observable<any> {
        return this.locationSetService.addChildLocations(parentTag, childTags);
    }

    public disableLocationSet(locationSetTag: string, disabled: boolean): Observable<any> {
        return this.locationSetService.disableLocationSet(locationSetTag, disabled);
    }

    public getSelectedOptionsByLocation(locationNumber): Observable<any> {
        return this.masterMenuSystemService.getMenuSelectedOptionsByLocation(locationNumber);
    }

    public getMenuSelectionOptionsByLocation(locationNumber): Observable<any> {
        return this.masterMenuSystemService.getMenuSelectionOptionsByLocation(locationNumber);
    }

    public saveSelectedOptions(selections, locationNumber, selectedEffectiveDate): Observable<any> {
        return this.masterMenuSystemService.saveSelectedOptions(selections, locationNumber, selectedEffectiveDate);
    }

    public clearCache(): Observable<any> {
        return this.menuService.deleteCache();
    }

    copyProdData(): Observable<any> {
        return this.menuService.copyProdData();
    }

    public getNutritionAssignments(location: string): Observable<NutritionAssignment[]> {
        return this.nutritionService.getProfileAssignments(location);
    }

    public getProfileOptions(): Observable<number[]> {
        return this.nutritionService.getProfileOptions();
    }

    public saveLocationAssignment(apiData: NutritionSaveRequest): Observable<void> {
        return this.nutritionService.saveLocationAssignment(apiData);
    }

    public createConsumer(consumer: Consumer): Observable<Consumer> {
        return this.consumerManagerService.createConsumer(consumer);
    }

    public duplicateConsumer(oldConsumerTag: string, newConsumerTag: string, newConsumerName: string): Observable<Consumer> {
        return this.consumerManagerService.duplicateConsumer(oldConsumerTag, newConsumerTag, newConsumerName);
    }

    public createConsumerCategory(consumerCategory: ConsumerCategory): Observable<ConsumerCategory> {
        return this.consumerManagerService.createConsumerCategory(consumerCategory);
    }

    public getPinOverridesGroups(): Observable<PinOverrideGroup[]> {
        return this.pinOverridesService.getPinOverrideGroups();
    }

    public getPinOverridesGroup(id: number): Observable<PinOverrideGroup> {
        return this.pinOverridesService.getPinOverrideGroup(id);
    }

    public deletePinOverridesGroupById(id: any): Observable<PinOverrideGroup> {
        return this.pinOverridesService.deletePinOverridesGroupById(id);
    }

    public savePinOverridesGroup(pinOverrideGroup: any): Observable<PinOverrideGroup> {
        return this.pinOverridesService.savePinOverrideGroup(pinOverrideGroup);
    }

    public deletePinOverrideById(id: any): Observable<PinOverride> {
        return this.pinOverridesService.deletePinOverrideById(id);
    }

    public publishActivePins(): Observable<any> {
        return this.pinOverridesService.publishActivePins();
    }

    public publishAllPins(forceOverride?: boolean, location?: string): Observable<any> {
        if (forceOverride && location) {
            return this.pinOverridesService.publishAllPins(forceOverride, location);
        } else {
            return this.pinOverridesService.publishAllPins();
        }
    }

    public getLanguageAssignment(location: any): Observable<LanguageAssignments> {
        return this.languageAssignmentsService.getLanguageAssignment(location);
    }

    public saveLanguageAssignment(languageAssignment: LanguageAssignments):
        Observable<LanguageAssignments> {
        return this.languageAssignmentsService.saveLanguageAssignment(languageAssignment);
    }

    public getAllItemIdMappingsBy(criteria: ItemIdMap): Observable<ItemIdMap[]> {
        return this.itemIdMappingsService.getAllItemIdMappingsBy(criteria);
    }

    public saveAllItemIdMappings(itemIdMappings: ItemIdMap[]): Observable<ItemIdMap[]> { // same method for create and update
        return this.itemIdMappingsService.saveAllItemIdMappings(itemIdMappings);
    }

    public deleteItemIdMapping(itemIdMappings: ItemIdMap): Observable<ItemIdMap[]> {
        return this.itemIdMappingsService.deleteItemIdMapping(itemIdMappings);
    }

    getVenuesByLocation(location: string): Observable<Venue[]> {
        return this.venueService.getVenuesByLocation(location);
    }

    getConsumersByVenue(venueId: string | number): Observable<Consumer[]> {
        return this.venueService.getConsumersByVenue(venueId);
    }

    getVenueMenu(location: string, venueNumber: string | number, effectiveDate?: string): Observable<Venue> {
        return this.venueService.getVenueMenu(location, venueNumber, effectiveDate);
    }

    getMenuItemForVenue(consumer: string, location: string, venueNumber: string | number, path?: string,
                        effectiveDate?: string): Observable<ItemDetail> {
        return this.venueService.getMenuItemForVenue(consumer, location, venueNumber, path, effectiveDate);
    }

    // tslint:disable-next-line:max-line-length
    getMenuItemChildrenForVenue(consumer: string, location: string, venueNumber: string | number, path?: string,
                                effectiveDate?: string): Observable<ItemSummary[]> {
        return this.venueService.getMenuItemChildrenForVenue(consumer, location, venueNumber, path, effectiveDate);
    }

    getAllSalesChannels(): Observable<SalesChannel[]> {
        return this.salesChannelsService.getAllSalesChannels();
    }

    renameSubmenu(submenuId: string, newName: string, effectiveDate?: string): Observable<any> {
        if (effectiveDate) {
            return this.menuService.renameSubmenu(submenuId, newName, effectiveDate);
        } else {
            return this.menuService.renameSubmenu(submenuId, newName);
        }
    }

    publishNutrition(locationType: string, location: {name: string, tag: string}) {
        if (locationType === 'CHAIN') {
            return this.nutritionPublishingService.publishChainwideNutrition();
        } else if (location) {
            return this.nutritionPublishingService.publishNutritionByStore(location.tag);
        }
    }
}
